import { Component, Element, Event, EventEmitter, h, Host, Prop } from '@stencil/core';
import { Dictionary } from '@wuic-core/types';
import {
  spriteFlags as flags,
  spriteUi as ui,
  spriteCircle as circle,
  spriteHelix as helix,
  spriteExtras as extras,
} from './assets';

/**
 * @readme
 * The WefoxIconSource Component is a Icon library loader/manager.
 *
 * ### Features
 * - already loaded with some predefined internal assets ( icons / flags )
 * - loads external assets via the `src` property
 * - loads embedded assets in code
 *
 * ### Avaliable Icons
 *
 * - Refer to the [KDS Core icons documentation](https://uberforcede.github.io/koble-web-ui/section-icons.html#kssref-icons-library)
 *
 * ### File format
 *
 * - The current format is a svg containing a set of `symbols`.
 * - Each symbol id will be used for reference in the wefox-icon icon property.
 *
 * ```typescript
 * <svg>
 *   ...
 *   <symbol id="my-icon-reference-name">
 *    ...
 *   </symbol>
 *   ...
 * </svg>
 * ```
 *
 *
 * ### Inlined icons
 *
 * ```html
 * <wefox-icon-source icon-set="demo">
 *   <svg>
 *     <symbol height="24" id="wf-svg-wefox-public-logo" viewBox="0 0 53 14">
 *       <path
 *         d="M10.323 13.434L8.827 9.06l-1.546 4.374H4.833L1.86 6.156C1.451..."
 *       ></path>
 *     </symbol>
 *   </svg>
 * </wefox-icon-source>
 * <wefox-icon icon="wf-svg-wefox-public-logo" icon-set="demo" size="large" />
 * ```
 * @wuicComponentHTMLExamples
 * Internal Standard Icons
 *
 * <wefox-icon-source src="&#x40;internal:icons"></wefox-icon-source>
 * <wefox-icon icon="archive-box" title="archive-box" icon-set="ui"/>
 * <wefox-icon icon="archive-drawer" title="archive-drawer" />
 * <wefox-icon icon="archive-folder" title="archive-folder" />
 * <wefox-icon icon="arrow-down" title="arrow-down" />
 * <wefox-icon icon="arrow-drop-down" title="arrow-drop-down" />
 *
 * Internal Flag Icons
 *
 * <wefox-icon-source src="&#x40;internal:icons"></wefox-icon-source>
 * <wefox-icon icon="FR" icon-set="flags" />
 * <wefox-icon icon="ES" icon-set="flags" />
 * <wefox-icon icon="GB" icon-set="flags" />
 * <wefox-icon icon="IT" icon-set="flags" />
 * <wefox-icon icon="DE" icon-set="flags" />
 *
 * Internal extra icons
 *
 * <wefox-icon-source src="&#x40;internal:icons"></wefox-icon-source>
 * <wefox-icon icon="loader" icon-set="extras" style={{ color:'var(--neutral-lightest)', stroke: 'var(--primary-base)' }} />
 *
 * Runtime Inlined icons
 *
 * <wefox-icon-source icon-set="demo">
 *   <svg>
 *     <symbol height="24" id="wf-svg-wefox-public-logo" viewBox="0 0 53 14">
 *       <path
 *         d="M10.323 13.434L8.827 9.06l-1.546 4.374H4.833L1.86 6.156C1.451 5.142 1.094 4.619.771 4.315v-.169h4.266v.169c-.374.304-.425.793-.034 1.84l1.224 3.26 2.005-5.269h1.139l2.074 5.1 1.138-3.09c.374-.997.34-1.537-.034-1.841v-.169h3.841v.169c-.238.304-.578.827-.986 1.84l-2.974 7.279h-2.107zM20.944 8.266c.866 0 1.427-.405 1.427-.98 0-.54-.458-.945-1.24-.945-.884 0-1.649.608-1.972 1.452.408.254.986.473 1.785.473m4.282 2.736c0 1.199-1.342 2.668-4.214 2.668-2.923 0-5.048-2.161-5.048-4.948 0-2.87 2.362-4.796 5.048-4.796 2.736 0 4.282 1.57 4.282 3.344 0 1.47-1.41 2.6-3.688 2.6-1.155 0-2.022-.287-2.549-.557.255 1.064 1.24 1.84 2.686 1.84 1.546 0 2.413-.793 2.906-1.4.272.185.577.607.577 1.249"
 *       ></path>
 *       <path fill="none" d="M31.7.076h-5.982v13.358H31.7V.076z"></path>
 *       <path
 *         d="M26.602 13.434v-.186c.459-.304.595-.81.595-1.824V6.51h-1.479V4.146h1.462a2.18 2.18 0 0 1-.544-1.452c0-1.588 1.19-2.618 2.855-2.618 1.224 0 2.108.76 2.108 1.655 0 .81-.442 1.284-1.054 1.385 0-.422-.221-.81-.68-.81-.357 0-.663.286-.663.776 0 .625.63 1.064 1.394 1.064H31.7V6.51h-1.529v4.914c0 1.013.17 1.52.595 1.824v.186h-4.164zM39.637 8.824c0-1.504-.986-2.314-2.244-2.314-1.257 0-2.243.81-2.243 2.314 0 1.469.986 2.313 2.243 2.313 1.258 0 2.244-.844 2.244-2.313m-2.244 4.846c-2.77 0-5.268-2.01-5.268-4.846 0-2.99 2.498-4.898 5.268-4.898 2.788 0 5.269 1.909 5.269 4.898 0 2.836-2.481 4.846-5.269 4.846"
 *       ></path>
 *       <path
 *         d="M48.27 13.434l-.017-.17c.407-.27.305-.658-.357-1.57l-.63-.861-.577.861c-.612.878-.646 1.3-.221 1.57v.17h-4.147v-.17c.238-.27.578-.709 1.224-1.57l2.21-2.972-2.04-2.803c-.663-.912-.97-1.334-1.343-1.604v-.169h4.538v.169c-.425.27-.306.692.357 1.604l.714.98.645-.98c.595-.895.646-1.334.187-1.604v-.169h4.164v.169c-.238.27-.56.743-1.206 1.604l-2.295 3.056 1.972 2.719c.662.912 1.155 1.3 1.529 1.57v.17H48.27z"
 *       ></path>
 *     </symbol>
 *   </svg>
 * </wefox-icon-source>
 * <wefox-icon icon="wf-svg-wefox-public-logo" icon-set="demo" size="large" />
 *
 * External icons
 *
 * <wefox-icon-source
 *   icon-set="demoExternal"
 *   src="data:image/svg+xml;base64,PHN2Zz48c3ltYm9sIHZpZXdCb3g9IjAgMCA1MTIgNTEyIiBpZD0iZGVtb0ljb24iPjxwYXRoIGQ9Ik00NTIgMzZINjBDMjYuOTE2IDM2IDAgNjIuOTE2IDAgOTZ2MjQwYzAgMzMuMDg0IDI2LjkxNiA2MCA2MCA2MGgxNzZ2NDBIMTMydjQwaDI0OHYtNDBIMjc2di00MGgxNzZjMzMuMDg0IDAgNjAtMjYuOTE2IDYwLTYwVjk2YzAtMzMuMDg0LTI2LjkxNi02MC02MC02MHptMjAgMzAwYzAgMTEuMDI4LTguOTcyIDIwLTIwIDIwSDYwYy0xMS4wMjggMC0yMC04Ljk3Mi0yMC0yMFY5NmMwLTExLjAyOCA4Ljk3Mi0yMCAyMC0yMGgzOTJjMTEuMDI4IDAgMjAgOC45NzIgMjAgMjB2MjQweiIvPjwvc3ltYm9sPjwvc3ZnPg=="
 * ></wefox-icon-source>
 * <wefox-icon icon="demoIcon" icon-set="demoExternal" size="large" />
 *
 */
@Component({
  tag: 'wefox-icon-source',
  styleUrl: 'wefox-icon-source.css',
  shadow: true,
})
export class WefoxIconSource {
  /** Loads SVG from network or internally using the &#x40;internal: protocol
   * @wuicHideExample
   * @wuicExampleValue data:image/svg+xml;base64,PHN2Zz48c3ltYm9sIHZpZXdCb3g9IjAgMCA1MTIgNTEyIiBpZD0iZGVtb0ljb24iPjxwYXRoIGQ9Ik00NTIgMzZINjBDMjYuOTE2IDM2IDAgNjIuOTE2IDAgOTZ2MjQwYzAgMzMuMDg0IDI2LjkxNiA2MCA2MCA2MGgxNzZ2NDBIMTMydjQwaDI0OHYtNDBIMjc2di00MGgxNzZjMzMuMDg0IDAgNjAtMjYuOTE2IDYwLTYwVjk2YzAtMzMuMDg0LTI2LjkxNi02MC02MC02MHptMjAgMzAwYzAgMTEuMDI4LTguOTcyIDIwLTIwIDIwSDYwYy0xMS4wMjggMC0yMC04Ljk3Mi0yMC0yMFY5NmMwLTExLjAyOCA4Ljk3Mi0yMCAyMC0yMGgzOTJjMTEuMDI4IDAgMjAgOC45NzIgMjAgMjB2MjQweiIvPjwvc3ltYm9sPjwvc3ZnPg==
   */
  @Prop() src: string = '';

  /**
   * Determines the iconSet name used to reference a group of icons/assets
   *
   * @wuicExampleValue demo-set
   * @wuicHideExample
   */
  @Prop({ mutable: true }) iconSet: string = 'ui';

  /**
   * The wefoxIconSourceIconsetLoaded event fires when svg's inside a iconSet are completely loaded
   * @wuicExample
   * el.addEventListener('wefoxIconSourceIconsetLoaded',(event:CustomEvent<string>)=>{
   *  trace('wefoxIconSourceIconsetLoaded',event);
   * });
   */
  @Event() wefoxIconSourceIconsetLoaded: EventEmitter<string>;

  /**
   * Contains the internal iconSet/icon metadata
   * WARNING: iconSet set to `icons` will be deprecated.
   */
  // eslint-disable-next-line @stencil/own-props-must-be-private
  public static icons: Dictionary<Dictionary<string>> = {};

  @Element() private el: HTMLWefoxIconSourceElement;

  componentWillLoad() {
    if (this.src !== '') {
      if (this.src.startsWith('@internal:')) {
        const [, iconSet] = this.src.split(':');
        const set = iconSet === 'icons' ? 'ui' : iconSet.replace(/-([a-z]{1})/g, (_, char) => char.toUpperCase());
        this.iconSet = set;
        this.updateIcons(this.iconSet, WefoxIconSource.internalAssets[this.iconSet], false);
      } else {
        this.fetchSVG();
      }
    } else {
      this.updateIcons(this.iconSet, this.el.innerHTML, true);
    }
  }

  render() {
    return (
      this.src && (
        <Host>
          <slot />
        </Host>
      )
    );
  }

  // eslint-disable-next-line class-methods-use-this
  private getSVG(content: string): Dictionary<string> {
    const domparser = new DOMParser();
    const result = content
      .replace(/[\n]/g, '')
      .match(/<symbol(.+?)\/symbol>/g)
      .map((symbol) => symbol.replace('<symbol ', '<svg ').replace('</symbol>', '</svg>'))
      .map((svg) => {
        const doc = domparser.parseFromString(`<div>${svg}</div>`, 'text/html');
        const el = doc.getElementsByTagName('svg')[0];
        el.setAttribute('width', '100%');
        el.setAttribute('height', '100%');
        return doc.getElementsByTagName('svg')[0].outerHTML;
      })
      .reduce((acc, svg) => {
        const id = svg.match(/<svg.+?id="(.+?)"/)[1];
        acc[id] = svg;
        return acc;
      }, {});

    return result;
  }

  private async fetchSVG() {
    const response = await fetch(this.src);
    response.text().then((svg) => {
      this.updateIcons(this.iconSet, svg, true);
    });
  }

  private static internalAssets = {
    flags,
    ui,
    circle,
    helix,
    extras,
  };

  private updateIcons(iconSet: string, svg: string | {}, parseIcons: boolean = false) {
    WefoxIconSource.icons[iconSet] = parseIcons ? this.getSVG(svg as string) : svg;
    this.wefoxIconSourceIconsetLoaded.emit(iconSet);
  }
}
